import { parameterUtils, type ModelData, type ModelParameter } from '@/shared/stores/model-utils'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

function updateHingePositions(model: ModelData, doorWidth: number, doorHeight: number) {
  const centerDoorPanel = getParam(model.parameters!, 'center_door_glass_panel')
  if (centerDoorPanel?.hardware?.hinges) {
    const updatedHinges = centerDoorPanel.hardware.hinges.map((hinge) => {
      if (hinge.positionId === 'hinge_top') {
        return {
          ...hinge,
          position: { ...hinge.position, x: doorWidth + 35, y: 0 },
        }
      } else if (hinge.positionId === 'hinge_bottom') {
        return {
          ...hinge,
          position: { ...hinge.position, x: doorWidth + 35, y: doorHeight - 108 },
        }
      }
      return hinge
    })

    updateParam(model, 'center_door_glass_panel', {
      ...centerDoorPanel,
      hardware: { ...centerDoorPanel.hardware, hinges: updatedHinges },
    })
  }
}

function updateHandlePosition(model: ModelData, _doorWidth: number, doorHeight: number) {
  const centerDoorPanel = getParam(model.parameters!, 'center_door_glass_panel')
  if (centerDoorPanel?.hardware?.handles) {
    const updatedHandles = centerDoorPanel.hardware.handles.map((handle) => {
      if (handle.positionId === 'vertical_handle') {
        return {
          ...handle,
          position: { ...handle.position, x: 50, y: doorHeight / 2 - 100 },
        }
      }
      return handle
    })

    updateParam(model, 'center_door_glass_panel', {
      ...centerDoorPanel,
      hardware: { ...centerDoorPanel.hardware, handles: updatedHandles },
    })
  }
}

const totalWidth: DimensionHandler = (model, _initial, numValue) => {
  model.width = numValue
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  const newDoorWidth = numValue - (leftFrame?.width || 0) - (rightFrame?.width || 0)

  updateParam(model, 'center_door_glass_panel', { width: newDoorWidth })
  model.doorConfiguration = {
    ...model.doorConfiguration!,
    totalDoorWidth: newDoorWidth,
    individualDoorWidth: [newDoorWidth],
  }

  updateHingePositions(model, newDoorWidth, model.height as number)

  // Update door panel position
  if (leftFrame) {
    updateParam(model, 'center_door_glass_panel', {
      position: { x: leftFrame.width, y: 0, z: 0 },
    })
  }

  return model
}

const totalHeight: DimensionHandler = (model, _initial, numValue) => {
  model.height = numValue
  updateParam(model, 'left_frame_profile', { height: numValue })
  updateParam(model, 'right_frame_profile', { height: numValue })
  updateParam(model, 'center_door_glass_panel', { height: numValue })

  const centerPanel = getParam(model.parameters!, 'center_door_glass_panel')
  if (centerPanel) {
    updateHingePositions(model, centerPanel.width, numValue)
    updateHandlePosition(model, centerPanel.width, numValue)
  }

  // Update door panel position
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  if (leftFrame) {
    updateParam(model, 'center_door_glass_panel', {
      position: { x: leftFrame.width, y: 0, z: 0 },
    })
  }

  return model
}

const doorWidth: DimensionHandler = (model, _initial, numValue) => {
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')

  updateParam(model, 'center_door_glass_panel', { width: numValue })
  model.width = numValue + (leftFrame?.width || 0) + (rightFrame?.width || 0)

  model.doorConfiguration = {
    ...model.doorConfiguration!,
    totalDoorWidth: numValue,
    individualDoorWidth: [numValue],
  }

  updateHingePositions(model, numValue, model.height as number)

  // Update door panel position
  if (leftFrame) {
    updateParam(model, 'center_door_glass_panel', {
      position: { x: leftFrame.width, y: 0, z: 0 },
    })
  }

  return model
}

const leftFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const centerPanel = getParam(model.parameters!, 'center_door_glass_panel')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')

  updateParam(model, 'left_frame_profile', { width: numValue })
  model.width = numValue + (centerPanel?.width || 0) + (rightFrame?.width || 0)

  if (centerPanel) {
    updateHingePositions(model, centerPanel.width, model.height as number)
  }

  // Update door panel position
  updateParam(model, 'center_door_glass_panel', {
    position: { x: numValue, y: 0, z: 0 },
  })

  return model
}

const rightFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const centerPanel = getParam(model.parameters!, 'center_door_glass_panel')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')

  updateParam(model, 'right_frame_profile', { width: numValue })
  model.width = (leftFrame?.width || 0) + (centerPanel?.width || 0) + numValue

  if (centerPanel) {
    updateHingePositions(model, centerPanel.width, model.height as number)
  }

  // Update door panel position
  if (leftFrame) {
    updateParam(model, 'center_door_glass_panel', {
      position: { x: leftFrame.width, y: 0, z: 0 },
    })
  }

  return model
}

export const model3DimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth,
  totalHeight,
  doorWidth,
  leftFrameWidth,
  rightFrameWidth,
}
